import { useEffect, useState } from "react";
import Fetch from "@/utils/Fetch";
import storage from "@/utils/storage";
import * as Enums from "@/utils/enums";
import helper from "@/utils/helper";
import constants from "@/utils/constants";
import { getUserFromCookie } from "@/utils/auth";

interface HubSpotTokenStore {
    token: string
    expiration: string
}

// Metadata key on AuthUserConfiguration (ConfigurationSection.None) used to persist the
// user's Show/Hide Help Chat preference.
const shownMetadataKey = 'showHsChat';

let scriptInjected = false;
// The last visibility the user asked for. Applied whenever the widget becomes ready,
// so a toggle made before HubSpot has finished loading still takes effect.
let desiredVisible = true;
// Set by showChatWidget(true) before HubSpot is ready; consumed by loadHubspot()'s ready callback.
let openWhenReady = false;

const visibilityEventName = 'hubspot-chat-visibility-change';

// Updates desiredVisible and notifies any useChatVisible() subscribers (e.g. ScDrawer,
// which removes its reserved bottom safe-area when the chat is hidden).
const setDesiredVisible = (visible: boolean) => {
    if (desiredVisible === visible) return;
    desiredVisible = visible;
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(visibilityEventName, { detail: { visible } }));
    }
};

const isChatVisible = () => desiredVisible;

// React hook for components that need to react to the chat's shown/hidden state (not just
// read it once) - e.g. to drop reserved layout space while the chat is hidden.
const useChatVisible = () => {
    const [visible, setVisible] = useState(isChatVisible());
    useEffect(() => {
        const handler = (e: any) => setVisible(!!e.detail?.visible);
        window.addEventListener(visibilityEventName, handler);
        return () => window.removeEventListener(visibilityEventName, handler);
    }, []);
    return visible;
};

// Ready-to-use bottom padding for anything reserving space so the floating widget doesn't
// cover its content: constants.chatWidgetSafeArea while shown, 0 while hidden. Centralises
// the `useChatVisible() ? constants.chatWidgetSafeArea : 0` pattern that would otherwise be
// repeated in every drawer/form that reserves this space (ScDrawer and its preferences
// drawers, ManageQuoteForm, ManageStockTransactionForm).
const useChatSafeAreaBottom = () => {
    const visible = useChatVisible();
    return visible ? constants.chatWidgetSafeArea : 0;
};

// Always read the live widget object instead of caching it - it does not exist until
// HubSpot's external script has actually finished loading, which can take an unknown
// amount of time after the script tag is inserted.
const getWidget = () => {
    if (typeof window === 'undefined') return undefined;
    return (window as any).HubSpotConversations?.widget;
};

// The widget renders at full size by default; this scales it down to match the compact
// look it's always had in this app. Re-applied every time the widget is (re)loaded rather
// than once on page mount, since HubSpot's own load timing (and now the delay while we
// wait to know the user's show/hide preference) is not bounded to any fixed window.
const applyCompactWidgetStyle = () => {
    if (typeof document === 'undefined') return;
    let count = 0;
    const interval = setInterval(() => {
        count++;
        const elem = document.getElementById('hubspot-messages-iframe-container');
        if (elem && elem.style) {
            // setProperty() merges into the existing inline style instead of replacing it.
            // HubSpot sets its own inline width/height on this element (its stylesheet only
            // covers position/z-index/bottom) - overwriting the whole style attribute wiped
            // those out, collapsing the container (and the 100%-sized iframe inside it) to
            // 0x0 and making the widget permanently invisible even though it had "loaded".
            elem.style.setProperty('right', '10px', 'important');
            elem.style.setProperty('transform-origin', '100% 100%');
            elem.style.setProperty('scale', '0.85');
            elem.style.setProperty('display', 'block');
            clearInterval(interval);
        }
        if (count > 50) {
            clearInterval(interval);
        }
    }, 100);
};

// Queues a callback on HubSpot's own documented ready-queue. HubSpot only drains this
// array once, the first time it becomes ready - it does not process anything pushed
// after that point. Only use this before the widget exists (getWidget() undefined); once
// it exists, call it directly instead.
const onReady = (callback: () => void) => {
    if (typeof window === 'undefined') return;
    const windowAny = window as any;
    windowAny.hsConversationsOnReady = windowAny.hsConversationsOnReady || [];
    windowAny.hsConversationsOnReady.push(callback);
};

const refreshHubSpotToken = async () => {

    let user = getUserFromCookie();

    if (!user) return null;
    if (user.email && user.email.trim().toLowerCase() === "admin") return null;

    let token = await Fetch.get({
        url: "/HubSpot/VisitorIdentificationToken",
    });

    if (!token) return null;

    let payload = helper.parseJwtPayload(token);
    if (!payload) return null;

    let tokenLifetimeSeconds = payload.exp - payload.iat;

    let tokenExpiration = new Date();
    tokenExpiration.setSeconds(tokenExpiration.getSeconds() + tokenLifetimeSeconds - (60 * 5)); //expire 5 minutes before actual expiry

    return {
        token: token,
        expiration: tokenExpiration.toISOString()
    } as HubSpotTokenStore;
}

const getLocalHubSpotToken = () => {

    let item = window.localStorage.getItem(Enums.LocalStorage.HubSpotToken);
    if (!!item) {
        return JSON.parse(item) as HubSpotTokenStore;
    }

    return null;
}

const isTokenExpired = (token: HubSpotTokenStore) => {

    if (!token) return true;

    var expirationDate = new Date(token.expiration);
    var now = new Date();

    return now.valueOf() >= expirationDate.valueOf();
}

const getHubSpotToken = async () => {

    // check if logged in, if not, return null
    if (!storage.hasCookieValue(Enums.Cookie.token)) return null;

    // get local token
    let localToken = getLocalHubSpotToken();

    // if expired, invalidate
    if (!!localToken && isTokenExpired(localToken)) {
        localToken = null;
    }

    // retrieve new token if not present
    if (!localToken) {
        localToken = await refreshHubSpotToken();

        // store in local storage
        if (!!localToken && typeof window !== "undefined") {
            window.localStorage.setItem(Enums.LocalStorage.HubSpotToken, JSON.stringify(localToken));
        }
    }

    return localToken?.token ?? null;
}

const initialiseChatWidget = async () => {
    let token = await getHubSpotToken();
    let userName = storage.getCookie(Enums.Cookie.servUserName);

    let windowAny = window as any;

    if (!!token && !!userName) {
        if (!windowAny.hsConversationsSettings) {
            windowAny.hsConversationsSettings = {
                loadImmediately: false
            };
        }
        windowAny.hsConversationsSettings.identificationEmail = userName;
        windowAny.hsConversationsSettings.identificationToken = token;
    }
    else if (windowAny.hsConversationsSettings) {
        windowAny.hsConversationsSettings.identificationEmail = undefined;
        windowAny.hsConversationsSettings.identificationToken = undefined;
    }

    // The token fetch above is async - a hide requested while it was in flight must win,
    // otherwise this load() would bring the widget straight back.
    if (!desiredVisible) return;

    // Check if HubSpotConversations is loaded before trying to access widget
    if (windowAny.HubSpotConversations?.widget) {
        windowAny.HubSpotConversations.widget.load();
        applyCompactWidgetStyle();
    }
}

const reInitialiseChatWidget = async () => {
    await initialiseChatWidget();
    let windowAny = window as any;
    let widgetHeight = document.getElementById("hubspot-messages-iframe-container")?.offsetHeight;

    // check if the widget is minimised (the height will be small) and if so, reset and reload it, otherwise do nothing as it will mess up the user if they are in the middle of a chat
    if (widgetHeight && widgetHeight < 150 && windowAny.HubSpotConversations?.resetAndReloadWidget) {
        windowAny.HubSpotConversations.resetAndReloadWidget();
    }
};

const clearChatWidget = async () => {

    let windowAny = window as any;

    // Clear our own cached identification token - it's an 8+ hour token (see
    // refreshHubSpotToken), so without this, a different user logging in on the same
    // browser before it expires would silently be identified to HubSpot as the PREVIOUS
    // user until getHubSpotToken() happens to refetch.
    if (typeof window !== "undefined") {
        window.localStorage.removeItem(Enums.LocalStorage.HubSpotToken);
    }

    // Never injected (chat hidden by preference) - there is no HubSpot session to reset, and
    // logout() awaits this, so waiting for HubSpotConversations would only stall the logout.
    if (!scriptInjected) return;

    // wait for HubSpot Conversations to be available
    let count = 0;
    while (!windowAny.HubSpotConversations && count < 50) {
        await helper.waitABit(100);
        count++;
    }

    if (windowAny.HubSpotConversations) {
        // clear local token
        if (windowAny.hsConversationsSettings) {
            windowAny.hsConversationsSettings.identificationEmail = undefined;
            windowAny.hsConversationsSettings.identificationToken = undefined;
        }
        windowAny.HubSpotConversations.resetAndReloadWidget();

        // resetAndReloadWidget() does its own network round-trip with HubSpot to reset the
        // visitor's identity. logout() navigates away (window.location.href) right after
        // calling this function - if that navigation fires before the reset actually
        // completes, the in-flight request is aborted mid-flight and HubSpot is left with a
        // half-reset visitor record. The next login's re-identification (same tab, same
        // browser) then never resolves: window.HubSpotConversations.widget.status() reports
        // pending indefinitely and no amount of local retrying (widget.load(),
        // resetAndReloadWidget() again, even reinjecting the <script> tag fresh) recovers it
        // - only an actual full page reload does. Giving the reset a head start here, and
        // logout() awaiting this whole function before navigating, avoids triggering that
        // state in the first place. Confirmed live against the real HubSpot CDN on
        // 2026-09-28/29.
        await helper.waitABit(500);
    }
}


const loadHubspot = async () => {

    if (typeof window === "undefined" || scriptInjected) return;
    scriptInjected = true;

    const _hsp = ((window as any)._hsp = (window as any)._hsp || []);
    _hsp.push(["addEnabledFeatureGates", []]);

    (window as any).hsConversationsSettings = {
        loadImmediately: false
    };

    onReady(async () => {
        console.log('HubSpot Conversations ready');
        // token stuff here
        await initialiseChatWidget();
        // Apply whatever visibility was last requested (may have been toggled before HubSpot was ready).
        if (!desiredVisible) {
            getWidget()?.remove();
        } else if (openWhenReady) {
            getWidget()?.open();
        }
        openWhenReady = false;
    });

    const loadHubspotScript = (function (t: any, e: any, r: any) {
        if (!(document as any).getElementById(t)) {
            const n = (document as any).createElement("script");
            for (const a in ((n.src = "https://js.usemessages.com/conversations-embed.js"), (n.type = "text/javascript"), (n.id = t), r)) r.hasOwnProperty(a) && n.setAttribute(a, r[a]);
            const i = (document as any).getElementsByTagName("script")[0];
            i.parentNode.insertBefore(n, i);


        }
    });

    loadHubspotScript("hubspot-messages-loader", 0, { "data-loader": "hs-scriptloader", "data-hsjs-portal": 8236184, "data-hsjs-env": "prod", "data-hsjs-hublet": "na1" });

}

// Hides the chat widget. HubSpot only drains window.hsConversationsOnReady once, at
// startup — pushing onto it after that point (the common case: the user toggles the
// setting long after the widget finished loading) never runs the callback. So act on the
// live widget immediately whenever it already exists, and only fall back to the
// ready-queue for the narrow window before HubSpot has loaded at all.
const hideChatWidget = () => {
    setDesiredVisible(false);
    const widget = getWidget();
    if (widget) {
        widget.remove();
    } else {
        onReady(() => getWidget()?.remove());
    }
};

// Shows (and optionally opens) the chat widget, loading HubSpot's script first if it was
// never injected because the widget started out hidden.
const showChatWidget = (open = false) => {
    setDesiredVisible(true);
    const widget = getWidget();
    if (widget) {
        // Re-sync identification (initialiseChatWidget re-fetches the token and re-sets
        // hsConversationsSettings before loading) instead of just widget.load(). The widget
        // object outlives a logout/login cycle within the same SPA session (no full page
        // reload), but identification is otherwise only ever set once, in loadHubspot()'s
        // one-time onReady callback - a plain widget.load() here would reuse whichever
        // user's (possibly since-cleared, on logout) identification was set at that first
        // load, leaving the widget stuck permanently pending after a different user logs in.
        initialiseChatWidget().then(() => {
            if (open && desiredVisible) widget.open();
        });
    } else {
        // Not ready yet: loadHubspot()'s own ready callback initialises (and loads) the widget
        // once desiredVisible is true - queueing a second initialise here would load it twice.
        if (open) openWhenReady = true;
        loadHubspot();
    }
};

export default {
    loadHubspot,
    reInitialiseChatWidget,
    clearChatWidget,
    hideChatWidget,
    showChatWidget,
    getWidget,
    isScriptInjected: () => scriptInjected,
    isChatVisible,
    useChatVisible,
    useChatSafeAreaBottom,
    shownMetadataKey
}

